import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n/LangContext";
import {
  Calendar, Wrench, Ship, Users as UsersIcon, UserCircle, FileText,
  LayoutDashboard, Settings, LogOut, Plus, Clock, Crown, ShieldCheck, Inbox
} from "lucide-react";

export default function AdminSidebar() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const nav = useNavigate();

  const mainNav = [
    { to: "/", Icon: LayoutDashboard, label: t("nav.dashboard"), end: true, color: "#2D62B7", bg: "#E8F1FF" },
    { to: "/calendario", Icon: Calendar, label: t("nav.calendario"), color: "#C2410C", bg: "#FFE6D7" },
    { to: "/trabajos", Icon: Wrench, label: t("nav.mantenimientos"), color: "#7C3AED", bg: "#F3E8FF" },
    { to: "/embarcaciones", Icon: Ship, label: t("nav.embarcaciones"), color: "#0369A1", bg: "#E0F2FE" },
    { to: "/mis-horas", Icon: Clock, label: t("nav.horas"), color: "#B45309", bg: "#FEF3C7" },
  ];
  const gestionNav = [
    { to: "/gestion/clientes", Icon: UsersIcon, label: t("nav.clientes"), color: "#2D62B7", bg: "#E8F1FF" },
    { to: "/gestion/servicios", Icon: Settings, label: t("nav.servicios"), color: "#C2410C", bg: "#FFE6D7" },
    { to: "/gestion/usuarios", Icon: UserCircle, label: t("nav.usuarios"), color: "#475569", bg: "#EEF0F4" },
    { to: "/gestion/albaranes", Icon: FileText, label: t("nav.albaranes"), color: "#116A34", bg: "#D5F5E0" },
    { to: "/gestion/ajustes", Icon: Settings, label: t("nav.ajustes"), color: "#7C3AED", bg: "#EDE9FE" },
  ];

  const superAdminNav = user?.is_super_admin ? [
    { to: "/super-admin/tenants", Icon: Crown, label: lang === "en" ? "App users" : "Usuarios app", color: "#B45309", bg: "#FEF3C7" },
    { to: "/gestion/leads", Icon: Inbox, label: "Leads", color: "#FF7A50", bg: "#FFE6D7" },
  ] : [];

  const renderItem = ({ to, Icon, label, end, color, bg }) => (
    <NavLink key={to} to={to} end={end}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${
          isActive
            ? "bg-white shadow-sm text-[var(--ink)] ring-1 ring-[var(--border)]"
            : "text-slate-600 hover:bg-white/60 hover:text-[var(--ink)]"
        }`
      }
      data-testid={`sb-${label.toLowerCase().replace(/\s+/g,'-')}`}
    >
      {({ isActive }) => (
        <>
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-transform group-hover:scale-105"
            style={{ background: isActive ? bg : "transparent", color: isActive ? color : "currentColor" }}
          >
            <Icon size={16} strokeWidth={2.3} />
          </div>
          <span className="truncate">{label}</span>
        </>
      )}
    </NavLink>
  );

  const doLogout = () => { logout(); nav("/login"); };

  return (
    <aside
      className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 flex-col z-30"
      style={{
        background: "linear-gradient(180deg, #FBFCFE 0%, #F2F4F8 100%)",
        borderRight: "1px solid var(--border)",
      }}
      data-testid="admin-sidebar"
    >
      <div className="px-5 py-5 flex items-center gap-3 border-b border-[var(--border)]">
        <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-sm ring-1 ring-[var(--border)]">
          <img src="/images/logo.svg" alt="S&S" className="w-10 h-10" />
        </div>
        <div>
          <div className="sns-title text-base leading-none">Smile &amp; Shine</div>
          <div className="text-[11px] text-slate-500 mt-1 font-semibold">Cleaning · Mallorca</div>
        </div>
      </div>

      <button
        onClick={() => nav("/nuevo-mantenimiento")}
        className="mx-4 mt-5 py-3 bg-[var(--coral)] hover:bg-[var(--coral-hover)] text-white rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-md shadow-[var(--coral)]/30 hover:shadow-lg"
        data-testid="sidebar-btn-nuevo">
        <Plus size={16} strokeWidth={2.5} /> {t("trabajos.new")}
      </button>

      <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-0.5">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold px-3 mb-2">{t("nav.principal")}</div>
        {mainNav.map(renderItem)}

        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold px-3 mt-6 mb-2">{t("nav.gestion")}</div>
        {gestionNav.map(renderItem)}

        {superAdminNav.length > 0 && (
          <>
            <div className="text-[10px] uppercase tracking-widest text-[var(--coral)] font-bold px-3 mt-6 mb-2 inline-flex items-center gap-1">
              <ShieldCheck size={11} /> Super Admin
            </div>
            {superAdminNav.map(renderItem)}
          </>
        )}
      </nav>

      <div className="p-3 border-t border-[var(--border)] space-y-2 bg-white/50">
        {/* Selector idioma rápido */}
        <div className="flex gap-1.5">
          <button
            onClick={() => setLang("es")}
            className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all ${lang === "es" ? "bg-[var(--ink)] text-white" : "bg-white border border-[var(--border)] text-slate-600"}`}
            data-testid="sb-lang-es"
          >
            🇪🇸 ES
          </button>
          <button
            onClick={() => setLang("en")}
            className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-all ${lang === "en" ? "bg-[var(--ink)] text-white" : "bg-white border border-[var(--border)] text-slate-600"}`}
            data-testid="sb-lang-en"
          >
            🇬🇧 EN
          </button>
        </div>
        <NavLink to="/perfil"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-xl transition-colors ${
              isActive ? "bg-white shadow-sm" : "hover:bg-white/70"
            }`
          }
        >
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-[var(--ink)] text-white flex items-center justify-center font-bold text-xs">{user?.name?.[0]}</div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold truncate text-[var(--ink)]">{user?.name}</div>
            <div className="text-[10px] text-slate-500 truncate">{user?.email}</div>
          </div>
        </NavLink>
        <button onClick={doLogout} className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors" data-testid="sidebar-btn-logout">
          <LogOut size={16} strokeWidth={2.2} /> {t("nav.logout")}
        </button>
      </div>
    </aside>
  );
}
